import {
  Code2,
  Copy,
  CreditCard,
  Eye,
  EyeOff,
  FileLock2,
  KeyRound,
  ShieldCheck,
  TerminalSquare,
} from 'lucide-react';
import { useState } from 'react';

import type { VaultItem } from '../../shared/model';

import { IconButton, Input, Textarea } from './ui';

export const vaultTypes = {
  login: { label: 'Login', icon: KeyRound, tone: 'info' },
  development: { label: 'Development secret', icon: Code2, tone: 'focus' },
  recovery: { label: 'Recovery codes', icon: ShieldCheck, tone: 'attention' },
  file: { label: 'Secure file', icon: FileLock2, tone: 'neutral' },
  card: { label: 'Credit card', icon: CreditCard, tone: 'success' },
  ssh: { label: 'SSH keys', icon: TerminalSquare, tone: 'priority' },
};

export function VaultTypeIcon({ type }: { type: VaultItem['type'] }) {
  const meta = vaultTypes[type ?? 'login'],
    Icon = meta.icon;

  return (
    <span className="vault-symbol" data-tone={meta.tone} title={meta.label}>
      <Icon aria-label={meta.label} />
    </span>
  );
}

function Field({
  label,
  value,
  onChange,
  locked,
  copy,
  secret = false,
  multiline = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  locked: boolean;
  copy: (value: string) => void;
  secret?: boolean;
  multiline?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);

  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the wrapped Input/Textarea carries aria-label; the rule does not look through the conditional
    <label>
      {label}
      <div className={`input-action ${multiline ? 'vault-multiline' : ''}`}>
        {multiline ? (
          <Textarea
            className={secret && !revealed ? 'vault-secret-masked' : undefined}
            aria-label={label}
            disabled={locked}
            rows={5}
            value={value}
            onChange={e => onChange(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        ) : (
          <Input
            aria-label={label}
            disabled={locked}
            type={secret && !revealed ? 'password' : 'text'}
            value={value}
            onChange={e => onChange(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        )}
        {secret && (
          <IconButton
            label={`${revealed ? 'Hide' : 'Reveal'} ${label.toLowerCase()}`}
            onClick={() => setRevealed(!revealed)}
          >
            {revealed ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </IconButton>
        )}
        <IconButton label={`Copy ${label.toLowerCase()}`} onClick={() => copy(value)}>
          <Copy aria-hidden="true" />
        </IconButton>
      </div>
    </label>
  );
}

export function VaultFields({
  draft,
  locked,
  onChange,
  copy,
}: {
  draft: VaultItem;
  locked: boolean;
  onChange: (patch: Partial<VaultItem>) => void;
  copy: (value: string) => void;
}) {
  const type = draft.type ?? 'login';
  const common = { locked, copy };

  const text = (
    key: 'username' | 'password' | 'url' | 'notes',
    label: string,
    secret = false,
    multiline = false,
  ) => (
    <Field
      key={key}
      {...common}
      label={label}
      value={draft[key]}
      onChange={value => onChange({ [key]: value })}
      secret={secret}
      multiline={multiline}
    />
  );

  const card = draft.card ?? { holder: '', number: '', expiry: '', cvv: '' };
  const ssh = draft.ssh ?? { publicKey: '', privateKey: '', passphrase: '', host: '' };

  return (
    <>
      {type === 'login' && (
        <>
          {text('username', 'Username')}
          {text('password', 'Password', true)}
          {text('url', 'Website')}
        </>
      )}
      {type === 'development' && (
        <>
          {text('username', 'Environment / identifier')}
          {text('password', 'Secret value', true, true)}
          {text('url', 'Service URL')}
        </>
      )}
      {type === 'recovery' && (
        <>
          {text('username', 'Account')}
          {text('url', 'Account URL')}
        </>
      )}
      {type === 'card' &&
        (['holder', 'number', 'expiry', 'cvv'] as const).map(key => (
          <Field
            key={key}
            {...common}
            label={
              {
                holder: 'Cardholder',
                number: 'Card number',
                expiry: 'Expiry (MM/YY)',
                cvv: 'CVV',
              }[key]
            }
            value={card[key]}
            secret={key === 'number' || key === 'cvv'}
            onChange={value => onChange({ card: { ...card, [key]: value } })}
          />
        ))}
      {type === 'ssh' && (
        <>
          {text('username', 'SSH username')}
          {(['host', 'publicKey', 'privateKey', 'passphrase'] as const).map(key => (
            <Field
              key={key}
              {...common}
              label={
                {
                  host: 'Host',
                  publicKey: 'Public key',
                  privateKey: 'Private key',
                  passphrase: 'Key passphrase',
                }[key]
              }
              value={ssh[key]}
              secret={key === 'privateKey' || key === 'passphrase'}
              multiline={key === 'publicKey' || key === 'privateKey'}
              onChange={value => onChange({ ssh: { ...ssh, [key]: value } })}
            />
          ))}
        </>
      )}
      {text(
        'notes',
        type === 'recovery' ? 'Recovery codes · one per line' : 'Secure notes',
        type === 'recovery',
        true,
      )}
    </>
  );
}
